import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { ImageOff, Loader2, Search, Trash2, X } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { BusyOverlay } from '@/components/ui/busy-overlay';
import { Button } from '@/components/ui/button';
import { Field, Section, Toggle } from '@/components/ui/editor-fields';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { fromPercent, toPercent } from '@/lib/format';
import { cn } from '@/lib/utils';
import { useCatalogSearch } from '@/features/modifiers/modifiers.api';
import {
  useDeletePosDiscount,
  useExcludedItems,
  useSavePosDiscount,
  useSetExcludedItem,
} from './pos-discounts.api';
import {
  DISCOUNT_TYPE,
  takesValue,
  type PosDiscountInput,
} from './types';

/**
 * A POS discount, and the items it is withheld from.
 *
 * The exclusion list is the part worth being careful about: a discount applies
 * to everything *except* what is listed here, which is the opposite of how a
 * list of products under a discount usually reads. The heading says so.
 *
 * Exclusions can only be edited once the discount exists — they are keyed on
 * its id — so on a new discount that section explains itself rather than
 * appearing broken.
 */
export function DiscountEditor({
  discount,
  open,
  onOpenChange,
}: {
  discount: PosDiscountInput | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();

  const [draft, setDraft] = useState<PosDiscountInput | null>(discount);
  const [itemQuery, setItemQuery] = useState('');
  /*
   * The percentage box keeps its own text.
   *
   * `discountValue` is a fraction, so the field has to show 10 for 0.1 — and
   * driving a number input straight off `toPercent(value)` eats the decimal
   * point as you type it: "0." parses to 0, re-renders as "0", and the point
   * is gone. Holding the text and converting on the way out lets someone type
   * 7.5 the way they would say it.
   */
  const [percentText, setPercentText] = useState('');

  const save = useSavePosDiscount();
  const remove = useDeletePosDiscount();
  const catalogSearch = useCatalogSearch();
  const excluded = useExcludedItems(discount?.id ?? null);
  const setExcluded = useSetExcludedItem(discount?.id ?? '');

  useEffect(() => {
    setDraft(discount);
    setItemQuery('');
    setPercentText(
      discount && discount.discountType === DISCOUNT_TYPE.percentageAmount
        ? String(toPercent(discount.discountValue))
        : ''
    );
    catalogSearch.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [discount]);

  if (!draft) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="right" className="w-full sm:max-w-lg" />
      </Sheet>
    );
  }

  const isEdit = !!draft.id;
  const busy = save.isPending || remove.isPending;
  const rows = excluded.data ?? [];
  // The exclusion list being re-read after an item is added or removed.
  const excludedBusy = excluded.isFetching && !excluded.isPending;

  const set = <K extends keyof PosDiscountInput>(
    key: K,
    value: PosDiscountInput[K]
  ) => setDraft((current) => (current ? { ...current, [key]: value } : current));

  /**
   * Changing the type clears the value.
   *
   * $10 off and 10% off are the same number under two meanings, so carrying it
   * across would silently turn a ten-dollar discount into a thousand-per-cent
   * one the moment somebody switched the select.
   */
  function setType(next: number) {
    setDraft((current) =>
      current ? { ...current, discountType: next, discountValue: 0 } : current
    );
    setPercentText('');
  }

  const needsValue = takesValue(draft.discountType);
  const canSave =
    !!draft.nameEn.trim() &&
    !!draft.nameEs.trim() &&
    (!needsValue || draft.discountValue > 0);

  const failed = (error: unknown) =>
    toast.error(
      error instanceof Error ? error.message : t('posDiscounts.saveFailed')
    );

  function submit() {
    save.mutate(draft!, {
      onSuccess: () => {
        toast.success(isEdit ? t('posDiscounts.updated') : t('posDiscounts.created'));
        onOpenChange(false);
      },
      onError: failed,
    });
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full gap-0 overflow-y-auto p-0 sm:max-w-lg"
      >
        <SheetHeader className="flex-row items-center justify-between gap-3 border-b pr-12">
          <SheetTitle>
            {isEdit
              ? t('posDiscounts.editDiscount')
              : t('posDiscounts.newDiscount')}
          </SheetTitle>
          {isEdit && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-destructive"
                  disabled={busy}
                >
                  <Trash2 className="size-3.5" />
                  {t('posDiscounts.delete')}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>
                    {t('posDiscounts.delete')}
                  </AlertDialogTitle>
                  <AlertDialogDescription>
                    {t('posDiscounts.deleteConfirm', { name: draft.nameEn })}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                  <AlertDialogAction
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    onClick={() =>
                      remove.mutate(draft.id!, {
                        onSuccess: () => {
                          toast.success(t('posDiscounts.deleted'));
                          onOpenChange(false);
                        },
                        onError: failed,
                      })
                    }
                  >
                    {t('posDiscounts.delete')}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </SheetHeader>

        <div className="space-y-6 p-4">
          <Section title={t('posDiscounts.details')}>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label={t('posDiscounts.nameEn')} htmlFor="nameEn">
                <Input
                  id="nameEn"
                  value={draft.nameEn}
                  disabled={busy}
                  onChange={(event) => set('nameEn', event.target.value)}
                />
              </Field>
              <Field label={t('posDiscounts.nameEs')} htmlFor="nameEs">
                <Input
                  id="nameEs"
                  value={draft.nameEs}
                  disabled={busy}
                  onChange={(event) => set('nameEs', event.target.value)}
                />
              </Field>
            </div>

            <Field
              label={t('posDiscounts.discountType')}
              hint={t('posDiscounts.discountTypeHint')}
            >
              <Select
                value={String(draft.discountType)}
                disabled={busy}
                onValueChange={(value) => setType(Number(value))}
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.values(DISCOUNT_TYPE).map((value) => (
                    <SelectItem key={value} value={String(value)}>
                      {t(`posDiscounts.type${value}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>

            {/* The three tax waivers carry no value; the field would be a box
                whose contents the server discards. */}
            {needsValue &&
              (draft.discountType === DISCOUNT_TYPE.percentageAmount ? (
                <Field
                  label={t('posDiscounts.percentage')}
                  htmlFor="discountValue"
                  hint={t('posDiscounts.percentageHint')}
                >
                  <Input
                    id="discountValue"
                    type="number"
                    min={0}
                    max={100}
                    step="0.1"
                    value={percentText}
                    disabled={busy}
                    onChange={(event) => {
                      const text = event.target.value;
                      setPercentText(text);
                      // An empty or half-typed box is zero rather than NaN,
                      // which would sail past `canSave` and post junk.
                      const parsed = Number(text);
                      set(
                        'discountValue',
                        text === '' || Number.isNaN(parsed)
                          ? 0
                          : fromPercent(parsed)
                      );
                    }}
                  />
                </Field>
              ) : (
                <Field
                  label={t('posDiscounts.amount')}
                  htmlFor="discountValue"
                >
                  <Input
                    id="discountValue"
                    type="number"
                    min={0}
                    step="0.01"
                    value={draft.discountValue}
                    disabled={busy}
                    onChange={(event) =>
                      set('discountValue', Number(event.target.value))
                    }
                  />
                </Field>
              ))}

            <Toggle
              label={t('posDiscounts.isActive')}
              hint={t('posDiscounts.isActiveHint')}
              checked={draft.isActive}
              disabled={busy}
              onChange={(value) => set('isActive', value)}
            />
            <Toggle
              label={t('posDiscounts.multiple')}
              hint={t('posDiscounts.multipleHint')}
              checked={draft.canBeAppliedMultipleTimes}
              disabled={busy}
              onChange={(value) => set('canBeAppliedMultipleTimes', value)}
            />
          </Section>

          {/* ------------------------------------------------- exclusions */}
          <Section title={t('posDiscounts.excludedItems')}>
            <p className="text-muted-foreground text-xs">
              {t('posDiscounts.excludedHint')}
            </p>

            {!isEdit ? (
              <p className="text-muted-foreground rounded-xl border border-dashed py-8 text-center text-sm">
                {t('posDiscounts.excludedAfterSave')}
              </p>
            ) : (
              <>
                <form
                  role="search"
                  className="flex gap-2"
                  onSubmit={(event) => {
                    event.preventDefault();
                    if (itemQuery.trim()) catalogSearch.mutate(itemQuery.trim());
                  }}
                >
                  <div className="relative flex-1">
                    <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2" />
                    <Input
                      value={itemQuery}
                      onChange={(event) => setItemQuery(event.target.value)}
                      placeholder={t('posDiscounts.searchItems')}
                      aria-label={t('posDiscounts.searchItems')}
                      className="pl-9"
                    />
                  </div>
                  <Button type="submit" disabled={catalogSearch.isPending}>
                    {catalogSearch.isPending ? (
                      <Loader2 className="animate-spin" />
                    ) : (
                      t('common.search')
                    )}
                  </Button>
                </form>

                {/* A new search clears the previous answer, so there is
                    nothing to keep on screen: stand in for the results. */}
                {catalogSearch.isPending && (
                  <div className="divide-y rounded-xl border">
                    {Array.from({ length: 3 }).map((_, index) => (
                      <div key={index} className="space-y-1.5 p-2.5">
                        <Skeleton className="h-3.5 w-2/3" />
                        <Skeleton className="h-3 w-1/3" />
                      </div>
                    ))}
                  </div>
                )}

                {catalogSearch.isSuccess &&
                  catalogSearch.data.items.length === 0 && (
                    <p className="text-muted-foreground text-xs">
                      {t('posDiscounts.noItemsFound')}
                    </p>
                  )}

                {catalogSearch.data && catalogSearch.data.items.length > 0 && (
                  <div className="max-h-48 divide-y overflow-y-auto rounded-xl border">
                    {catalogSearch.data.items.map((item) => {
                      const already = rows.some(
                        (row) => row.itemId === item.id || row.item?.id === item.id
                      );
                      return (
                        <div
                          key={item.id}
                          className="flex items-center gap-2.5 p-2.5 text-sm"
                        >
                          <div className="min-w-0 flex-1">
                            <p className="truncate">{item.name || '—'}</p>
                            <p className="text-muted-foreground truncate text-xs">
                              {item.brand}
                            </p>
                          </div>
                          <Button
                            size="sm"
                            variant={already ? 'outline' : 'default'}
                            disabled={already || setExcluded.isPending}
                            onClick={() =>
                              setExcluded.mutate(
                                { itemId: item.id, remove: false },
                                { onError: failed }
                              )
                            }
                          >
                            {already
                              ? t('posDiscounts.excluded')
                              : t('posDiscounts.exclude')}
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                )}

                {excluded.isPending && (
                  <div className="divide-y rounded-xl border">
                    {Array.from({ length: 2 }).map((_, index) => (
                      <div key={index} className="flex items-center gap-2.5 p-3">
                        <Skeleton className="size-9 shrink-0 rounded-md" />
                        <div className="flex-1 space-y-1.5">
                          <Skeleton className="h-3.5 w-2/3" />
                          <Skeleton className="h-3 w-1/3" />
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {excluded.isSuccess && rows.length === 0 && (
                  <div className="relative rounded-xl">
                    <BusyOverlay show={excludedBusy} />
                    <p className="text-muted-foreground rounded-xl border border-dashed py-8 text-center text-sm">
                      {t('posDiscounts.noExclusions')}
                    </p>
                  </div>
                )}

                {rows.length > 0 && (
                  <div className="relative rounded-xl">
                    <BusyOverlay show={excludedBusy} />
                    <div
                      aria-busy={excludedBusy}
                      className={cn(
                        'divide-y rounded-xl border',
                        excludedBusy && 'opacity-60 transition-opacity'
                      )}
                    >
                      {rows.map((row) => (
                        <div
                          key={row.id}
                          className="flex items-center gap-2.5 p-3 text-sm"
                        >
                          {row.item?.itemThumbnail || row.item?.itemImage ? (
                            <img
                              src={row.item.itemThumbnail ?? row.item.itemImage ?? ''}
                              alt=""
                              className="bg-muted size-9 shrink-0 rounded-md object-contain"
                            />
                          ) : (
                            <div className="bg-muted text-muted-foreground flex size-9 shrink-0 items-center justify-center rounded-md">
                              <ImageOff className="size-3.5" />
                            </div>
                          )}
                          <div className="min-w-0 flex-1">
                            <p className="truncate">{row.item?.name || '—'}</p>
                            <p className="text-muted-foreground truncate text-xs">
                              {[row.item?.brand, row.item?.gtin]
                                .filter(Boolean)
                                .join(' · ')}
                            </p>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            aria-label={t('posDiscounts.removeExclusion')}
                            disabled={setExcluded.isPending}
                            onClick={() =>
                              setExcluded.mutate(
                                { itemId: row.itemId ?? row.item?.id ?? '', remove: true },
                                { onError: failed }
                              )
                            }
                          >
                            <X className="size-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </Section>

          <div className="flex justify-end gap-2 pt-1">
            <Button
              variant="outline"
              disabled={busy}
              onClick={() => onOpenChange(false)}
            >
              {t('common.cancel')}
            </Button>
            <Button disabled={!canSave || busy} onClick={submit}>
              {save.isPending && <Loader2 className="animate-spin" />}
              {isEdit ? t('common.save') : t('posDiscounts.create')}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
